import { memo, Fragment, useState, useEffect } from 'react';
import css from './index.module.scss';
import {
  BoxContainer,
  SelectLabel,
  TextField,
  RectifyRemarksContainer,
  AutoComplete
} from '@components/common';
import { Controller, useFormContext } from 'react-hook-form';
import { SUPPLY_TYPE } from '@helpers/constants';
import { getEnquiryList } from '@services/quote.service';
import notify from '@helpers/toastify-helper';
import { IQuoteEnquiry } from '@helpers/types/add-order';
import { Button } from '@components/base';

interface TypeOfOrderContainerProps {
  rectifyRemarks?: string;
}

const TypeOfOrderContainer = (props: TypeOfOrderContainerProps) => {
  const { control, setValue, watch } = useFormContext();
  const { rectifyRemarks } = props;

  const [enquiryState, setEnquiryState] = useState<{
    options: IQuoteEnquiry[];
    isLoading: boolean;
    isOpen: boolean;
  }>({
    options: [],
    isLoading: false,
    isOpen: false
  });

  const selectedEnquiry = watch('quote_enquiry');

  const formatEnquiryOptions = (options: any[]) => {
    return options.map((enquiry: any) => {
      const dealNamePart = enquiry.deal_name ? ` (${enquiry.deal_name})` : '';
      return {
        ...enquiry,
        display_label: `${enquiry.customer_name} - ${enquiry.readable_enquiry_number}${dealNamePart}`
      };
    });
  };

  useEffect(() => {
    // Load initial enquiry list when component mounts
    const loadInitialEnquiries = async () => {
      setEnquiryState((prevState) => ({
        ...prevState,
        isLoading: true
      }));

      const response = await getEnquiryList({
        is_draft: false
      });

      if (response?.data?.results) {
        const formattedOptions = formatEnquiryOptions(response.data.results);
        setEnquiryState((prevState) => ({
          ...prevState,
          isLoading: false,
          options: formattedOptions
        }));
      } else {
        setEnquiryState((prevState) => ({
          ...prevState,
          isLoading: false,
          options: []
        }));
        if (response?.error) {
          notify({
            message: response.error ?? 'Unable to fetch enquiry list',
            severity: 'error'
          });
        }
      }
    };

    loadInitialEnquiries();
  }, []);

  const handleEnquiryInputChange = async (event: any, searchText: any) => {
    if (!searchText) {
      setEnquiryState((prevState) => ({
        ...prevState,
        isOpen: false,
        isLoading: false
      }));
      return;
    }

    setEnquiryState((prevState) => ({
      ...prevState,
      isLoading: true
    }));

    const response = await getEnquiryList({
      is_draft: false,
      search: searchText
    });

    if (response?.data?.results) {
      const formattedOptions = formatEnquiryOptions(response.data.results);
      setEnquiryState((prevState) => ({
        ...prevState,
        isLoading: false,
        options: formattedOptions,
        isOpen: true
      }));
    } else {
      setEnquiryState((prevState) => ({
        ...prevState,
        isLoading: false,
        options: [],
        isOpen: true
      }));
      if (response?.error) {
        notify({
          message: response.error ?? 'Unable to fetch enquiry list',
          severity: 'error'
        });
      }
    }
  };

  const handleEnquirySelection = (event: any, selectedValue: any) => {
    if (selectedValue) {
      setValue('quote_enquiry', selectedValue, { shouldValidate: true });
    } else {
      setValue('quote_enquiry', null, { shouldValidate: true });
    }
    setEnquiryState((prevState) => ({
      ...prevState,
      isOpen: false,
      isLoading: false
    }));
  };

  const handleOutsideClick = () => {
    setEnquiryState((prevState) => ({
      ...prevState,
      isOpen: false
    }));
  };

  return (
    <>
      {rectifyRemarks ? (
        <RectifyRemarksContainer rectifyRemarks={rectifyRemarks} module="Order" />
      ) : (
        <></>
      )}

      <BoxContainer title="Type of Order">
        <div className={css.boxWrapper}>
          <div className={css.rowWrapper}>
            <Controller
              name="supply_type"
              control={control}
              render={({ field, fieldState }) => (
                <SelectLabel
                  {...field}
                  autoFocus
                  required
                  options={SUPPLY_TYPE}
                  label="Select the Type of Order"
                  rootClassName={css.fieldWrapper}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="readable_order_id"
              control={control}
              render={({ field }) => (
                <TextField {...field} disabled label="Order Number" placeholder="Order ID" />
              )}
            />
          </div>
          <div className={css.rowWrapper}>
            <Controller
              name="quote_enquiry"
              control={control}
              render={({ field, fieldState }) => (
                <div className={css.quoteFieldWrapper}>
                  <AutoComplete
                    {...field}
                    required
                    label="Reference Quote"
                    placeholder="Start typing to search"
                    onClickAway={handleOutsideClick}
                    options={enquiryState.options}
                    onInputChange={handleEnquiryInputChange}
                    onInputSelection={handleEnquirySelection}
                    isOpen={enquiryState.isOpen}
                    isLoading={enquiryState.isLoading}
                    keyOption="display_label"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    width="100%"
                  />
                  {selectedEnquiry && (
                    <Button
                      variant="text"
                      className={css.quoteLink}
                      onClick={() =>
                        window.open(`/quote/detail/${selectedEnquiry.enquiry_id}`, '_blank')
                      }
                    >
                      View Quote →
                    </Button>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </BoxContainer>
    </>
  );
};

export default memo(TypeOfOrderContainer);
